<template>
  <div class="mc-setting-auto-ended-quick-assignment">
    <div class="mc-setting-auto-ended-quick-assignment__header">
      <h5 class="mc-setting-auto-ended-quick-assignment__title">
        {{ texts.title }}
      </h5>

      <div class="mc-setting-auto-ended-quick-assignment__actions">
        <McButton
          variant="secondary"
          icon="icon-outline_close"
          :text="texts.cancel"
          :disabled="!hasSettingChanged || isLoading"
          @click="handleCancel"
        />
        <McButton
          variant="primary"
          icon="icon-outline_save"
          :text="texts.save"
          :disabled="!hasSettingChanged || isLoading"
          @click="handleSave"
        />
      </div>
    </div>

    <div class="mc-setting-auto-ended-quick-assignment__content">
      <section class="mc-setting-auto-ended-quick-assignment__card">
        <h5 class="mc-setting-auto-ended-quick-assignment__card-title">
          {{ texts.tripAutomation }}
        </h5>
        <p class="mc-setting-auto-ended-quick-assignment__description">
          {{ texts.tripAutomationDescription }}
        </p>
      </section>

      <section class="mc-setting-auto-ended-quick-assignment__card">
        <h5 class="mc-setting-auto-ended-quick-assignment__card-title">
          {{ texts.completeAutomation }}
        </h5>
        <p class="mc-setting-auto-ended-quick-assignment__description">
          {{ texts.completeAutomationDescription }}
        </p>

        <div class="mc-setting-auto-ended-quick-assignment__rules">
          <span class="mc-setting-auto-ended-quick-assignment__rules-title">
            {{ texts.additionalRules }}
          </span>

          <div class="mc-setting-auto-ended-quick-assignment__rule">
            <div class="mc-setting-auto-ended-quick-assignment__rule-copy">
              <McCheckbox
                id="require-all-locations-visited"
                v-model="autoEndedAllVisited"
                :label="texts.requireAllLocationsVisited"
              />
              <span class="mc-setting-auto-ended-quick-assignment__helper">
                {{ texts.defaultActiveStatus }}
              </span>
            </div>
          </div>

          <div class="mc-setting-auto-ended-quick-assignment__rule">
            <div class="mc-setting-auto-ended-quick-assignment__rule-main">
              <div class="mc-setting-auto-ended-quick-assignment__rule-copy">
                <McCheckbox
                  id="use-max-active-limit"
                  v-model="autoEndedDurationEnabled"
                  :label="texts.useMaxActiveLimit"
                />

                <span class="mc-setting-auto-ended-quick-assignment__helper">
                  {{ texts.defaultActiveStatus }}
                </span>
              </div>

              <div
                v-if="autoEndedDurationEnabled"
                class="mc-setting-auto-ended-quick-assignment__inline-control"
              >
                <div class="mc-setting-auto-ended-quick-assignment__input-group">
                  <input
                    v-model.number="completeAfterDays"
                    class="mc-setting-auto-ended-quick-assignment__days-input"
                    :class="{
                      'mc-setting-auto-ended-quick-assignment__days-input--error':
                        showDurationValidationError,
                    }"
                    type="number"
                    :min="minActiveDays"
                    :max="maxActiveDays"
                    @input="handleDurationInput"
                    @blur="handleDurationBlur"
                  />
                  <span
                    v-if="showDurationValidationError"
                    class="mc-setting-auto-ended-quick-assignment__error"
                  >
                    {{ durationValidationErrorText }}
                  </span>
                </div>
                <span class="mc-setting-auto-ended-quick-assignment__inline-text">
                  {{ texts.completeAfterDaysSuffix }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import isEqual from 'lodash/isEqual'

import McButton from '@/components/McButton'
import McCheckbox from '@/components/McCheckbox'
import * as Setting from '@/services/rest-api/lib/fleet-task/setting'

const MIN_ACTIVE_DAYS = 7
const MAX_ACTIVE_DAYS = 30
const ACTIVITIES_COMPLETION = 'ACTIVITIES_COMPLETION'

export default {
  components: {
    McButton,
    McCheckbox,
  },
  inject: ['setting', 'getUserSetting'],
  data() {
    return {
      autoEndedToLastPoint: true,
      autoEndedAllVisited: true,
      autoEndedDurationEnabled: false,
      completeAfterDays: MIN_ACTIVE_DAYS,
      durationInputTouched: false,
      initialSetting: null,
      isLoading: false,
      minActiveDays: MIN_ACTIVE_DAYS,
      maxActiveDays: MAX_ACTIVE_DAYS,
    }
  },
  computed: {
    texts() {
      return {
        title: this.$t('fleetTaskInstant.autoEndedQuickAssigment.label'),
        cancel: this.$t('fleetTaskInstant.autoEndedQuickAssigment.cancelButton'),
        save: this.$t('fleetTaskInstant.autoEndedQuickAssigment.saveButton'),
        tripAutomation: this.$t('fleetTaskInstant.autoEndedQuickAssigment.tripAutomation'),
        tripAutomationDescription: this.$t(
          'fleetTaskInstant.autoEndedQuickAssigment.tripAutomationDescription'
        ),
        completeAutomation: this.$t('fleetTaskInstant.autoEndedQuickAssigment.completeAutomation'),
        completeAutomationDescription: this.$t(
          'fleetTaskInstant.autoEndedQuickAssigment.completeAutomationDescription'
        ),
        additionalRules: this.$t('fleetTaskInstant.autoEndedQuickAssigment.additionalRules'),
        requireAllLocationsVisited: this.$t(
          'fleetTaskInstant.autoEndedQuickAssigment.requireAllLocationsVisited'
        ),
        useMaxActiveLimit: this.$t('fleetTaskInstant.autoEndedQuickAssigment.useMaxActiveLimit'),
        completeAfterDaysPrefix: this.$t(
          'fleetTaskInstant.autoEndedQuickAssigment.completeAfterDaysPrefix'
        ),
        completeAfterDaysSuffix: this.$t(
          'fleetTaskInstant.autoEndedQuickAssigment.completeAfterDaysSuffix'
        ),
        defaultActiveStatus: this.$t(
          'fleetTaskInstant.autoEndedQuickAssigment.defaultActiveStatus'
        ),
        defaultInactiveStatus: this.$t(
          'fleetTaskInstant.autoEndedQuickAssigment.defaultInactiveStatus'
        ),
      }
    },
    hasSettingChanged() {
      if (!this.initialSetting) {
        return false
      }

      return !isEqual(this.initialSetting, this.getCurrentForm())
    },
    isDurationInvalid() {
      if (!this.autoEndedDurationEnabled) {
        return false
      }

      const parsedValue = Number(this.completeAfterDays)

      if (this.completeAfterDays === '' || Number.isNaN(parsedValue)) {
        return true
      }

      return parsedValue < this.minActiveDays || parsedValue > this.maxActiveDays
    },
    showDurationValidationError() {
      return this.durationInputTouched && this.isDurationInvalid
    },
    durationValidationErrorText() {
      const parsedValue = Number(this.completeAfterDays)

      if (this.completeAfterDays === '' || Number.isNaN(parsedValue)) {
        return this.$t('fleetTaskInstant.autoEndedQuickAssigment.validation.minimumDays')
      }

      if (parsedValue < this.minActiveDays) {
        return this.$t('fleetTaskInstant.autoEndedQuickAssigment.validation.minimumDays')
      }

      if (parsedValue > this.maxActiveDays) {
        return this.$t('fleetTaskInstant.autoEndedQuickAssigment.validation.maximumDays')
      }

      return ''
    },
  },
  watch: {
    setting: {
      immediate: true,
      handler(value) {
        if (!value) return

        this.applySetting(value)
      },
    },
    autoEndedDurationEnabled(value) {
      if (value) {
        return
      }

      this.completeAfterDays = MIN_ACTIVE_DAYS
      this.durationInputTouched = false
    },
  },
  methods: {
    applySetting(setting) {
      const normalizedSetting = this.normalizeSetting(setting)


      this.autoEndedToLastPoint = normalizedSetting.autoEndedToLastPoint
      this.autoEndedAllVisited = normalizedSetting.autoEndedAllVisited
      this.autoEndedDurationEnabled = normalizedSetting.autoEndedDurationEnabled
      this.completeAfterDays = normalizedSetting.completeAfterDays
      this.durationInputTouched = false
      this.initialSetting = { ...normalizedSetting }
    },
    normalizeSetting(setting = {}) {
      return {
        autoEndedToLastPoint: setting.auto_ended_default.auto_ended_to_last_point ?? true,
        autoEndedAllVisited:
          setting.auto_ended_default.auto_end_type === ACTIVITIES_COMPLETION || !!setting.auto_ended_default.auto_ended_all_visited,
        autoEndedDurationEnabled: Boolean(setting.auto_ended_default.auto_ended_duration),
        completeAfterDays: this.parseDurationDays(setting.auto_ended_default.auto_ended_duration),
      }
    },
    parseDurationDays(duration) {
      if (typeof duration !== 'string') {
        return MIN_ACTIVE_DAYS
      }

      const parsedValue = Number(duration.replace('P', '').replace('D', ''))
      if (Number.isNaN(parsedValue)) {
        return MIN_ACTIVE_DAYS
      }

      return parsedValue
    },
    getCurrentForm() {
      return {
        autoEndedToLastPoint: this.autoEndedToLastPoint,
        autoEndedAllVisited: this.autoEndedAllVisited,
        autoEndedDurationEnabled: this.autoEndedDurationEnabled,
        completeAfterDays: this.completeAfterDays,
      }
    },
    getPayload() {
      return {
        auto_ended_all_visited: this.autoEndedAllVisited,
        auto_ended_duration: this.autoEndedDurationEnabled ? `P${Number(this.completeAfterDays)}D` : null,
      }
    },
    handleDurationInput() {
      this.durationInputTouched = true
    },
    handleDurationBlur() {
      this.durationInputTouched = true
    },
    handleCancel() {
      if (!this.initialSetting) {
        return
      }

      this.autoEndedToLastPoint = this.initialSetting.autoEndedToLastPoint
      this.autoEndedAllVisited = this.initialSetting.autoEndedAllVisited
      this.autoEndedDurationEnabled = this.initialSetting.autoEndedDurationEnabled
      this.completeAfterDays = this.initialSetting.completeAfterDays
      this.durationInputTouched = false
    },
    async handleSave() {
      this.isLoading = true

      if (this.isDurationInvalid) {
        this.durationInputTouched = true
        this.isLoading = false
        return
      }

      try {
        const res = await Setting.update(this.getPayload())

        if (res?.errors) {
          this.$mcToast.error(
            this.$t('fleetTaskInstant.autoEndedQuickAssigment.message.automationSettingsFailed')
          )
          return
        }

        this.$mcToast.success(
          this.$t('fleetTaskInstant.autoEndedQuickAssigment.message.automationSettingsSaved')
        )

        await this.getUserSetting()
      } catch (error) {
        this.$mcToast.error(
          this.$t('fleetTaskInstant.autoEndedQuickAssigment.message.automationSettingsFailed')
        )
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>

<style scoped lang="scss">
.mc-setting-auto-ended-quick-assignment {
  display: flex;
  flex-direction: column;
  gap: 16px;

  &__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
  }

  &__title {
    margin: 0;
    color: $teal;
    font-size: 18px;
    font-weight: 600;
    line-height: 32px;
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__card {
    border: 1px solid $teal-100;
    border-radius: 5px;
    background: $white;
    padding: 16px 20px;
  }

  &__card-title {
    margin: 0;
    color: $teal;
    font-size: 16px;
    font-weight: 600;
    line-height: 24px;
  }

  &__description {
    margin: 4px 0 0;
    color: #667085;
    font-size: 14px;
    line-height: 22px;
  }

  &__rules {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 16px;
  }

  &__rules-title {
    color: $teal;
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
  }

  &__rule {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__rule-main {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    flex-wrap: wrap;
  }

  &__rule-copy {
    display: flex;
    flex-direction: column;
  }

  &__helper {
    color: $teal-300;
    font-size: 12px;
    line-height: 18px;
    margin-top: -5px;
    padding-left: 16px;
  }

  &__inline-control {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  &__inline-text {
    color: $teal-300;
    font-size: 12px;
    line-height: 20px;
    white-space: nowrap;
  }

  &__input-group {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  &__days-input {
    width: 68px;
    height: 28px;
    padding: 4px 10px;
    border: 1px solid #d0d5dd;
    border-radius: 4px;
    color: #344054;
    font-size: 12px;
    line-height: 18px;

    &:focus {
      outline: none;
    }
  }

  &__days-input--error {
    border-color: $red;
  }

  &__error {
    color: $red;
    font-size: 12px;
    line-height: 18px;
    white-space: nowrap;
  }
}

:deep(.mc-setting-auto-ended-quick-assignment .custom-checkbox) {
  display: inline-flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 0;
}

:deep(.mc-setting-auto-ended-quick-assignment .custom-control-label) {
  margin-left: 0;
  color: #344054;
  font-size: 14px;
  line-height: 20px;
}

:deep(.mc-setting-auto-ended-quick-assignment .custom-control-label::before),
:deep(.mc-setting-auto-ended-quick-assignment .custom-control-label::after) {
  top: 2px;
}

@media (max-width: 768px) {
  .mc-setting-auto-ended-quick-assignment {
    &__title {
      font-size: 20px;
      line-height: 28px;
    }

    &__header,
    &__rule-main {
      align-items: stretch;
    }

    &__actions {
      width: 100%;
    }

    &__helper {
      padding-left: 0;
    }
  }
}
</style>
